// Workload Intelligence + Work Health (work-management-service, Phase 1).
// Read-only views over the caller's OWN work and — when the tenant's Work
// policy grants workload visibility — the people in the caller's workload
// scope. Every number describes WORK (counts of items, a relative band with
// its reasons), never the person; the UI copy honours that. The server
// resolves the population + `item_visibility` per person: the client never
// filters people, and never shows titles the server withheld.
import { gatewayApi } from '@/lib/hrApi';
import type { EffortSize, WorkHealth, WorkStatus, WorkloadBand, WorkloadConfidence } from './workService';

const WL = '/api/v1/workload';

export interface WorkloadPerson {
  employee_id: string;
  user_id?: string | null;
  display_name: string;
  job_title?: string | null;
  department_id?: string | null;
  department_name?: string | null;
  avatar_url?: string | null;
}

/** Counts over one person's work. Every field is a number of work items. */
export interface WorkloadSummary {
  active: number;
  upcoming: number;
  paused: number;
  active_subitems: number;
  due_soon: number;
  overdue: number;
  awaiting_review: number;
  stale: number;
  collaborating: number;
  review_queue: number;
}

/** The relative band + the reasons that produced it (always shown together). */
export interface WorkloadBandView {
  band: WorkloadBand;
  score: number;
  confidence: WorkloadConfidence;
  reasons: string[];
}

/** One work item that needs attention, with its health + explanation. */
export interface AttentionItem {
  id: string;
  title: string;
  status: WorkStatus;
  owner_employee_id?: string | null;
  owner_name?: string | null;
  parent_id?: string | null;
  planned_end_at?: string | null;
  progress: number;
  primary_health: WorkHealth;
  signals: string[];
  explain: string[];
  last_meaningful_activity_at?: string | null;
  effort_size?: EffortSize | null;
}

/** How much of a person's work the caller may see: full titles, some, or
 * counts only (`none` → never render titles). */
export type ItemVisibility = 'full' | 'partial' | 'none';

/** Phase 2 — capacity vs. planned load for ONE person. Present only when an
 * admin configured that person's capacity; `null` = not configured, and the
 * UI must then say "Capacity not set" (never a percentage). Every field is in
 * relative effort units per week (the effort-weight scale), never hours.
 * `over_capacity` describes the PLAN, not the person. */
export interface WorkloadCapacityView {
  units: number;
  load_units: number;
  headroom_units: number;
  utilization_pct: number;
  over_capacity: boolean;
}

/** How "stale after N days" counts days. `business_days` uses the company's
 * rest days and public holidays from Time Off; client copy must switch
 * ("calendar days" / "business days") — server `explain[]` strings already do. */
export type StaleBasis = 'calendar_days' | 'business_days';

export interface WorkloadPolicyView {
  stale_after_days: number;
  due_soon_days: number;
  /** Default `calendar_days` (older backends omit it). */
  stale_basis: StaleBasis;
  workload_enabled: boolean;
}

export interface MyWorkload {
  person: WorkloadPerson;
  summary: WorkloadSummary;
  workload: WorkloadBandView;
  attention: AttentionItem[];
  policy: WorkloadPolicyView;
  capacity: WorkloadCapacityView | null;
}

export interface WorkloadTotals {
  active: number;
  upcoming?: number;
  paused: number;
  due_soon: number;
  overdue: number;
  awaiting_review: number;
  stale: number;
  collaborating?: number;
}

export interface WorkloadScopeSummary {
  population: number;
  totals: WorkloadTotals;
  generated_at: string;
  /** Phase 2: how many people in scope have capacity configured, and how many
   * of those have a plan over their capacity. Absent on older backends. */
  capacity?: { configured_people: number; over_capacity: number } | null;
}

export interface WorkloadPersonRow {
  person: WorkloadPerson;
  summary: WorkloadSummary;
  workload: WorkloadBandView;
  item_visibility: ItemVisibility;
  capacity: WorkloadCapacityView | null;
}

/** `headroom`: configured people by headroom descending, then unconfigured. */
export type WorkloadSort = 'workload' | 'overdue' | 'due_soon' | 'name' | 'headroom';

export interface WorkloadPeopleParams {
  q?: string;
  department_id?: string;
  health?: WorkHealth;
  sort?: WorkloadSort;
  page?: number;
  page_size?: number;
}

export interface WorkloadPeoplePage {
  items: WorkloadPersonRow[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
  /** Human label of the caller's workload reach ("your direct reports", …). */
  scope_summary: string;
}

export interface PersonWorkload {
  person: WorkloadPerson;
  summary: WorkloadSummary;
  workload: WorkloadBandView;
  attention: AttentionItem[];
  item_visibility: ItemVisibility;
  capacity: WorkloadCapacityView | null;
}

export interface WorkloadDepartmentRow {
  department_id: string;
  department_name: string;
  head_employee_id?: string | null;
  people: number;
  totals: WorkloadTotals;
}

export interface AttentionParams {
  health?: WorkHealth;
  department_id?: string;
  employee_id?: string;
  page?: number;
  page_size?: number;
}

export interface AttentionPage {
  items: AttentionItem[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

// ---- Capacity configuration (admin, work:manage). One row per person whose
// capacity an admin set; people without a row are simply "not configured".
export interface CapacityItem {
  employee_id: string;
  display_name: string;
  job_title?: string | null;
  department_name?: string | null;
  /** Relative effort units per week (same scale as effort weights), never hours. */
  capacity_units: number;
  note: string | null;
  updated_at: string;
}

export interface CapacityParams {
  q?: string;
  page?: number;
  page_size?: number;
}

export interface CapacityPage {
  items: CapacityItem[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface CapacityBody {
  /** > 0 and ≤ 1000 — validated client-side too (`validateCapacityUnits`). */
  capacity_units: number;
  note?: string;
}

export const CAPACITY_UNITS_MIN = 0;
export const CAPACITY_UNITS_MAX = 1000;

/** Parses a units input; returns the number or `null` when it is not a
 * number in (0, 1000]. */
export const validateCapacityUnits = (raw: string | number): number | null => {
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw).trim());
  if (!Number.isFinite(n) || n <= CAPACITY_UNITS_MIN || n > CAPACITY_UNITS_MAX) return null;
  return n;
};

// ---- Phase 3 (Intelligence): trends + suggested moves. Both are READ-ONLY
// descriptions of work; a suggestion changes nothing until the user reassigns
// through the existing POST /work-items/{id}/reassign flow.

/** One nightly snapshot day. Days without a snapshot are simply absent. */
export interface WorkloadTrendDay {
  day: string;
  people: number;
  active: number;
  overdue: number;
  due_soon: number;
  stale: number;
  awaiting_review: number;
  load_units: number;
  /** Null when nobody in the population had capacity configured that day. */
  capacity_units: number | null;
  /** Load of the configured people only — the honest numerator for "load vs capacity". */
  configured_load_units?: number | null;
}

export interface WorkloadTrends {
  from: string;
  to: string;
  /** False until the first nightly snapshot ran — the UI shows an empty state. */
  snapshots_available: boolean;
  days: WorkloadTrendDay[];
}

export interface TrendsParams {
  /** Self or within reach (403/404 otherwise); omit for the viewer's population. */
  employee_id?: string;
  department_id?: string;
  days?: number;
}

export interface SuggestionPerson {
  employee_id: string;
  display_name: string;
  job_title?: string | null;
  department_name?: string | null;
  band: WorkloadBand;
  /** Null = capacity not configured — never show a percentage then. */
  capacity: WorkloadCapacityView | null;
}

export interface SuggestionCandidate extends SuggestionPerson {
  why: string[];
}

export interface WorkloadSuggestion {
  id: string;
  from: SuggestionPerson;
  item: { id: string; title: string; effort_size?: EffortSize | null; planned_end_at?: string | null; status: WorkStatus };
  candidates: SuggestionCandidate[];
  why: string[];
}

export interface WorkloadSuggestions {
  generated_at: string;
  /** Server footnote (e.g. how candidates were chosen) — rendered verbatim. */
  note: string;
  items: WorkloadSuggestion[];
}

export interface SuggestionsParams {
  department_id?: string;
  limit?: number;
}

const emptySummary = (): WorkloadSummary => ({
  active: 0, upcoming: 0, paused: 0, active_subitems: 0, due_soon: 0, overdue: 0,
  awaiting_review: 0, stale: 0, collaborating: 0, review_queue: 0,
});

const emptyBand = (): WorkloadBandView => ({ band: 'NORMAL', score: 0, confidence: 'counts_only', reasons: [] });

/** A capacity view is trusted only when it is a complete object — a missing,
 * null or partial payload (older backend) reads as "not configured". */
const normalizeCapacity = (c: unknown): WorkloadCapacityView | null => {
  if (!c || typeof c !== 'object') return null;
  const v = c as Partial<WorkloadCapacityView>;
  if (typeof v.units !== 'number' || v.units <= 0) return null;
  return {
    units: v.units,
    load_units: typeof v.load_units === 'number' ? v.load_units : 0,
    headroom_units: typeof v.headroom_units === 'number' ? v.headroom_units : v.units - (typeof v.load_units === 'number' ? v.load_units : 0),
    utilization_pct: typeof v.utilization_pct === 'number' ? v.utilization_pct : Math.round(((typeof v.load_units === 'number' ? v.load_units : 0) / v.units) * 100),
    over_capacity: !!v.over_capacity,
  };
};

const strip = <T extends object>(params: T): Partial<T> =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)) as Partial<T>;

export const getMyWorkload = async (): Promise<MyWorkload> => {
  const d = (await gatewayApi.get<Partial<MyWorkload>>(`${WL}/me`)).data || {};
  return {
    person: d.person as WorkloadPerson,
    summary: { ...emptySummary(), ...(d.summary || {}) },
    workload: { ...emptyBand(), ...(d.workload || {}), reasons: d.workload?.reasons || [] },
    attention: d.attention || [],
    policy: { stale_after_days: 5, due_soon_days: 3, stale_basis: 'calendar_days', workload_enabled: true, ...(d.policy || {}) },
    capacity: normalizeCapacity(d.capacity),
  };
};

export const getWorkloadSummary = async (params: { department_id?: string } = {}): Promise<WorkloadScopeSummary> =>
  (await gatewayApi.get<WorkloadScopeSummary>(`${WL}/summary`, { params: strip(params) })).data;

/** 403 (with a plain `message`) when the caller has no workload visibility
 * beyond self — the page surfaces that message instead of an empty table. */
export const listWorkloadPeople = async (params: WorkloadPeopleParams = {}): Promise<WorkloadPeoplePage> => {
  const d = (await gatewayApi.get<Partial<WorkloadPeoplePage>>(`${WL}/people`, { params: strip(params) })).data || {};
  return {
    items: (d.items || []).map((r) => ({
      ...r,
      summary: { ...emptySummary(), ...(r.summary || {}) },
      workload: { ...emptyBand(), ...(r.workload || {}), reasons: r.workload?.reasons || [] },
      item_visibility: r.item_visibility || 'none',
      capacity: normalizeCapacity(r.capacity),
    })),
    total: d.total ?? (d.items || []).length,
    page: d.page ?? params.page ?? 1,
    page_size: d.page_size ?? params.page_size ?? 20,
    has_more: !!d.has_more,
    scope_summary: d.scope_summary || '',
  };
};

export const getPersonWorkload = async (employeeId: string): Promise<PersonWorkload> => {
  const d = (await gatewayApi.get<Partial<PersonWorkload>>(`${WL}/people/${employeeId}`)).data || {};
  return {
    person: d.person as WorkloadPerson,
    summary: { ...emptySummary(), ...(d.summary || {}) },
    workload: { ...emptyBand(), ...(d.workload || {}), reasons: d.workload?.reasons || [] },
    attention: d.attention || [],
    item_visibility: d.item_visibility || 'none',
    capacity: normalizeCapacity(d.capacity),
  };
};

export const listWorkloadDepartments = async (): Promise<WorkloadDepartmentRow[]> =>
  (await gatewayApi.get<{ items?: WorkloadDepartmentRow[] | null }>(`${WL}/departments`)).data?.items || [];

export const listAttention = async (params: AttentionParams = {}): Promise<AttentionPage> => {
  const d = (await gatewayApi.get<Partial<AttentionPage>>(`${WL}/attention`, { params: strip(params) })).data || {};
  return {
    items: d.items || [],
    total: d.total ?? (d.items || []).length,
    page: d.page ?? params.page ?? 1,
    page_size: d.page_size ?? params.page_size ?? 20,
    has_more: !!d.has_more,
  };
};

// ---- Capacity configuration (work:manage).
export const listCapacity = async (params: CapacityParams = {}): Promise<CapacityPage> => {
  const d = (await gatewayApi.get<Partial<CapacityPage>>(`${WL}/capacity`, { params: strip(params) })).data || {};
  return {
    items: d.items || [],
    total: d.total ?? (d.items || []).length,
    page: d.page ?? params.page ?? 1,
    page_size: d.page_size ?? params.page_size ?? 20,
    has_more: !!d.has_more,
  };
};

export const putCapacity = async (employeeId: string, body: CapacityBody): Promise<CapacityItem> =>
  (await gatewayApi.put<CapacityItem>(`${WL}/capacity/${employeeId}`, body)).data;

export const deleteCapacity = async (employeeId: string): Promise<void> => {
  await gatewayApi.delete(`${WL}/capacity/${employeeId}`);
};

/** Phase 3: write today's snapshot rows for the tenant now (work:manage) — the
 *  manual form of the nightly tick, so trends can start the same day. */
export const runWorkloadSnapshot = async (): Promise<{ rows: number; day: string }> =>
  (await gatewayApi.post<{ rows: number; day: string }>(`${WL}/snapshot/run`)).data;

// ---- Phase 3: trends (self or population) + suggestions (team reach; 403 otherwise).
export const getWorkloadTrends = async (params: TrendsParams = {}): Promise<WorkloadTrends> => {
  const d = (await gatewayApi.get<Partial<WorkloadTrends>>(`${WL}/trends`, { params: strip({ days: 30, ...params }) })).data || {};
  return {
    from: d.from || '',
    to: d.to || '',
    snapshots_available: !!d.snapshots_available,
    days: (d.days || []).map((x) => ({
      ...x,
      capacity_units: typeof x.capacity_units === 'number' ? x.capacity_units : null,
      configured_load_units: typeof x.configured_load_units === 'number' ? x.configured_load_units : null,
    })),
  };
};

export const getWorkloadSuggestions = async (params: SuggestionsParams = {}): Promise<WorkloadSuggestions> => {
  const d = (await gatewayApi.get<Partial<WorkloadSuggestions>>(`${WL}/suggestions`, { params: strip({ limit: 10, ...params }) })).data || {};
  return {
    generated_at: d.generated_at || '',
    note: d.note || '',
    items: (d.items || []).map((s) => ({
      ...s,
      why: s.why || [],
      from: { ...s.from, capacity: normalizeCapacity(s.from?.capacity) },
      candidates: (s.candidates || []).map((c) => ({ ...c, why: c.why || [], capacity: normalizeCapacity(c.capacity) })),
    })),
  };
};
